import { useMutation, useQuery } from '@tanstack/react-query';
import { requestHandler } from 'lib/api';
import { errorHandler } from 'lib/functions/errors';
import { Dispatch } from 'redux';

import { HTTP_METHODS, TIME } from '../consts';
import { BASE_ROUTE, MUTATION_KEYS } from './consts';
import {
  BatchMarkAsLostProcess,
  CancelProcess,
  ClaimProcess,
  CreateForm,
  EmitWarrantyInvoiceBody,
  InvoicingReponse,
  MarkAsLostProcess,
  PaymentsResponse,
  UpdateForm,
} from './types';

export const getSGRWarrantyRequests = async (
  dispatch: Dispatch,
  query: string,
) => {
  const response = await requestHandler({
    method: HTTP_METHODS.GET,
    url: `${BASE_ROUTE}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessSGRWarrantyRequests = async (
  dispatch: Dispatch,
  businessIdentifier: string,
  query: string,
) => {
  const response = await requestHandler({
    method: HTTP_METHODS.GET,
    url: `${BASE_ROUTE}/${businessIdentifier}?${query}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSGRWarrantyRequestFile = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: [...MUTATION_KEYS.GET_FILE, id],
    queryFn: async (): Promise<Blob | undefined> => {
      const response = await requestHandler({
        method: HTTP_METHODS.GET,
        url: `${BASE_ROUTE}/${id}/file`,
        dispatch,
        responseType: 'blob',
      });
      if (response) return response.data;
    },
    enabled: !!id,
    retry: false,
    staleTime: TIME.MINUTE * 5,
  });

export const getSGRWarrantyRequestPayments = (dispatch: Dispatch, id: number) =>
  useQuery({
    queryKey: [...MUTATION_KEYS.GET_PAYMENTS, id],
    queryFn: async (): Promise<PaymentsResponse | undefined> => {
      const response = await requestHandler({
        method: HTTP_METHODS.GET,
        url: `${BASE_ROUTE}/${id}/payments`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    staleTime: TIME.MINUTE * 5,
  });

export const getSGRWarrantyRequestInvoicing = (
  dispatch: Dispatch,
  id: number,
) =>
  useQuery({
    queryKey: [...MUTATION_KEYS.GET_INVOICING, id],
    queryFn: async (): Promise<InvoicingReponse | undefined> => {
      const response = await requestHandler({
        method: HTTP_METHODS.GET,
        url: `${BASE_ROUTE}/${id}/invoicing`,
        dispatch,
      });
      if (response) return response.data;
    },
    retry: 1,
    staleTime: TIME.MINUTE * 5,
  });

export const createSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.CREATE,
    mutationFn: async (data: CreateForm) => {
      const response = await requestHandler({
        method: HTTP_METHODS.POST,
        url: BASE_ROUTE,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const emitWarrantyInvoice = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.EMIT_INVOICE,
    mutationFn: async (body: EmitWarrantyInvoiceBody) => {
      const response = await requestHandler({
        method: HTTP_METHODS.POST,
        url: `${BASE_ROUTE}/invoice`,
        data: body,
        dispatch,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const updateSGRWarrantyRequest = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.UPDATE,
    mutationFn: async (params: { id: number; data: UpdateForm }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const startClaimProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.START_CLAIM,
    mutationFn: async (params: { id: number; data: ClaimProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}/claim`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const markAsLostProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.MARK_AS_LOST,
    mutationFn: async (params: { id: number; data: MarkAsLostProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}/lost`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const markAsLostBatchProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: ['sgr', 'mark', 'lost', 'batch'],
    mutationFn: async (data: BatchMarkAsLostProcess) => {
      const response = await requestHandler({
        method: 'put',
        url: `internal/sgrWarrantyRequest/lost/batch`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const reverseMarkAsLostProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.REVERSE_MARK_AS_LOST,
    mutationFn: async (params: { id: number }) => {
      const { id } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}/lost/reverse`,
        dispatch,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const cancelProcess = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.SGR_CANCEL,
    mutationFn: async (params: { id: number; data: CancelProcess }) => {
      const { id, data } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}/cancel`,
        dispatch,
        data,
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const updateOpportunityStatus = (dispatch: Dispatch) =>
  useMutation({
    mutationKey: MUTATION_KEYS.UPDATE_OPPORTUNITY_STATUS,
    mutationFn: async (params: {
      id: number;
      status: 'LEAD' | 'IN_MANAGEMENT' | 'CONTACTED' | 'TO_CLOSE';
    }) => {
      const { id, status } = params;
      const response = await requestHandler({
        method: HTTP_METHODS.PUT,
        url: `${BASE_ROUTE}/${id}/opportunity`,
        dispatch,
        data: { status },
      });
      if (response) return response.data;
    },
    onError: (error) => errorHandler(dispatch, error),
  });

export const deleteSGRWarrantyRequest = async (
  dispatch: Dispatch,
  id: number,
) => {
  const response = await requestHandler({
    method: HTTP_METHODS.DELETE,
    url: `${BASE_ROUTE}/${id}`,
    dispatch,
  });

  if (response) return response.data;
};
